// Child-theme JS entry.

/**
 * Secondary ("hamburger") nav in the header — see inc/nav-secondary.php for
 * why this exists (Ekko has no desktop secondary nav; the design does).
 *
 * Kept to plain DOM APIs rather than jQuery: the panel markup is appended by
 * a PHP filter, so there is no theme script to hook into, and this needs to
 * work whether or not Ekko's own nav JS has run.
 */
function initSecondaryNav() {
	const toggle = document.querySelector('.hs-hamburger__toggle');
	const panel = document.getElementById('hs-hamburger-panel');

	if (!toggle || !panel) {
		return;
	}

	const setOpen = (open) => {
		toggle.setAttribute('aria-expanded', String(open));
		panel.hidden = !open;
		toggle.classList.toggle('is-open', open);
	};

	toggle.addEventListener('click', (event) => {
		event.preventDefault();
		event.stopPropagation();
		setOpen(toggle.getAttribute('aria-expanded') !== 'true');
	});

	// Click outside closes it.
	document.addEventListener('click', (event) => {
		if (panel.hidden) {
			return;
		}
		if (!panel.contains(event.target) && event.target !== toggle) {
			setOpen(false);
		}
	});

	// Escape closes it and returns focus to the toggle.
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !panel.hidden) {
			setOpen(false);
			toggle.focus();
		}
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initSecondaryNav);
} else {
	initSecondaryNav();
}

/**
 * Sub-menu disclosures inside the secondary nav panel.
 * inc/nav-secondary.php's hs_prepare_hamburger_submenus() renders every
 * branch's `hidden` attribute server-side (open only for the current page's
 * own branch) and gives each parent a `.hs-hamburger__submenu-toggle` button
 * wired to its `.sub-menu` via `aria-controls`; this just flips both back
 * and forth on click.
 */
function initSecondaryNavSubmenus() {
	const toggles = document.querySelectorAll('.hs-hamburger__submenu-toggle');

	toggles.forEach((toggle) => {
		const submenu = document.getElementById(toggle.getAttribute('aria-controls'));

		if (!submenu) {
			return;
		}

		toggle.addEventListener('click', (event) => {
			// Ekko's own script (core/assets/js/scripts.js) collapses the
			// whole mobile menu on any document click that isn't its own
			// `.mobile-dropdown` span — this button isn't that, so without
			// stopping propagation here every tap opened the branch and
			// closed the entire nav in the same instant.
			event.stopPropagation();

			const open = toggle.getAttribute('aria-expanded') !== 'true';
			toggle.setAttribute('aria-expanded', String(open));
			submenu.hidden = !open;
		});
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initSecondaryNavSubmenus);
} else {
	initSecondaryNavSubmenus();
}

/**
 * A CSS time — `"0.34s"`, `"120ms"` — as milliseconds.
 *
 * `getComputedStyle` returns a comma-separated list when an element carries
 * several animations; only the first is ours, and taking it is what makes
 * this safe on an element some other stylesheet also animates.
 *
 * @param {string} value Computed `animation-duration` / `animation-delay`.
 * @return {number} Milliseconds, or 0 for anything unparseable.
 */
function cssTimeToMs(value) {
	const first = String(value || '').split(',')[0].trim();
	const amount = parseFloat(first);

	if (!Number.isFinite(amount)) {
		return 0;
	}

	return first.endsWith('ms') ? amount : amount * 1000;
}

/**
 * Gallery — load-more, and the lightbox. PLAN.md §7z, markup in
 * `template-parts/gallery.php`.
 *
 * **Why this opens PhotoSwipe itself.** The parent theme has a delegated
 * handler for `a[data-size]` (`ekko/core/assets/js/scripts.js`), but it
 * captures `window.PhotoSwipe` into a local *at parse time* and only then
 * binds. A gallery rendered in the body enqueues the library after
 * `ekko-scripts` has been printed, so that local is `undefined` and the
 * handler does nothing — true for the theme's own `tek_photogallery` too.
 * Reading `window.PhotoSwipe` at click time is the whole fix.
 *
 * The listener is on `document` in the **capture** phase and stops
 * propagation, so on a page where the theme's handler *is* alive (a builder
 * page carrying a `tek_photogallery` that enqueued PhotoSwipe early enough)
 * the click never reaches it and the lightbox opens once, not twice.
 *
 * With no PhotoSwipe at all, nothing is prevented and the link opens the
 * full-size image — the gallery degrades to what it would be without JS.
 */
function initGalleries() {
	const galleries = document.querySelectorAll('[data-hs-gallery]');

	if (!galleries.length) {
		return;
	}

	galleries.forEach((gallery) => {
		const button = gallery.querySelector('[data-hs-gallery-more]');

		if (!button) {
			return;
		}

		// Rendered `hidden` so a control that cannot work is never shown; see
		// the template part's `<noscript>` for the JS-off path.
		button.hidden = false;

		const step = parseInt(gallery.dataset.hsGalleryStep, 10) || 8;

		// Longest stagger the reveal is allowed to run for, in steps. A
		// `step` of 8 never reaches it; an element configured with a much
		// larger one would otherwise leave the last thumbs arriving a second
		// or more after the click, which reads as jank rather than as motion.
		const maxStagger = 7;

		button.addEventListener('click', () => {
			const extras = [...gallery.querySelectorAll('.hs-gallery__item[hidden]')];
			const batch = extras.slice(0, step);

			batch.forEach((item, index) => {
				item.hidden = false;

				// The animation is the stylesheet's; this only says *when*
				// each item starts. `--hs-reveal-index` is read by
				// `components/_gallery.scss`, which also carries the
				// `prefers-reduced-motion` opt-out — so there is nothing to
				// check here, a reduced-motion user simply gets a keyframe
				// that does not move.
				item.style.setProperty('--hs-reveal-index', String(Math.min(index, maxStagger)));
				item.classList.add('hs-gallery__item--revealing');

				// One-shot: the class is what plays the animation, so it goes
				// again once it has played, leaving the thumb an ordinary
				// grid item.
				//
				// `animationend` is the signal, but it is not a guarantee —
				// it never fires when the animation resolves to `none` (a
				// stylesheet that did not load, a UA that suppresses motion
				// wholesale), and a browser that never composites the frames
				// does not fire it either. So the timer below is the one that
				// is always armed and `animationend` is the early exit;
				// `done()` only removes things, so running it twice is a
				// no-op. Its length is read back off the element rather than
				// repeated here, which keeps `_gallery.scss` the only place
				// the timing is written down.
				const style = getComputedStyle(item);
				const runtime = cssTimeToMs(style.animationDuration) + cssTimeToMs(style.animationDelay);

				const done = () => {
					item.classList.remove('hs-gallery__item--revealing');
					item.style.removeProperty('--hs-reveal-index');
				};

				if (style.animationName === 'none' || runtime === 0) {
					done();
				} else {
					item.addEventListener('animationend', done, { once: true });
					window.setTimeout(done, runtime + 120);
				}
			});

			// Nothing left to reveal — the control goes rather than sitting
			// there doing nothing.
			if (extras.length <= step) {
				button.remove();
				const wrap = gallery.querySelector('.hs-gallery__more');
				if (wrap) {
					wrap.remove();
				}
				return;
			}

			// Focus the first newly revealed thumb: a keyboard user who
			// activated the control should land on what it produced, not stay
			// on a button that has moved down the page.
			const revealed = extras[0].querySelector('.hs-gallery__link');
			if (revealed) {
				revealed.focus({ preventScroll: true });
			}
		});
	});

	document.addEventListener(
		'click',
		(event) => {
			const link = event.target.closest && event.target.closest('.hs-gallery__link');

			if (!link) {
				return;
			}

			const PhotoSwipe = window.PhotoSwipe;
			const PhotoSwipeUI = window.PhotoSwipeUI_Default;
			const pswpElement = document.querySelector('.pswp');

			if (!PhotoSwipe || !PhotoSwipeUI || !pswpElement) {
				return;
			}

			const gallery = link.closest('[data-hs-gallery]');

			if (!gallery) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			// Every link in the grid, hidden ones included: the point of the
			// lightbox is to slide through the whole gallery, not through the
			// eight that happen to be on screen.
			const links = [...gallery.querySelectorAll('.hs-gallery__link')];

			const items = links.map((el) => {
				const size = (el.dataset.size || '').split('x');
				const img = el.querySelector('img');
				const caption = el.dataset.captionTitle;

				return {
					src: el.getAttribute('href'),
					w: parseInt(size[0], 10) || 0,
					h: parseInt(size[1], 10) || 0,
					msrc: img ? img.currentSrc || img.src : undefined,
					title: caption ? `<div class="pswp__caption__title">${caption}</div>` : undefined,
					el,
				};
			});

			const options = {
				index: links.indexOf(link),
				showHideOpacity: true,
				history: false,
				// A thumb that is still `hidden` has no box to zoom from —
				// returning nothing makes PhotoSwipe fade instead of animating
				// from 0,0, which is what the theme's own handler does wrong.
				getThumbBoundsFn: (index) => {
					const img = items[index].el.querySelector('img');

					if (!img || !img.getClientRects().length) {
						return null;
					}

					const rect = img.getBoundingClientRect();

					return { x: rect.left, y: rect.top + window.pageYOffset, w: rect.width };
				},
			};

			new PhotoSwipe(pswpElement, PhotoSwipeUI, items, options).init();
		},
		true
	);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initGalleries);
} else {
	initGalleries();
}

/**
 * Repair `tek_map`'s race with the Google Maps loader — PLAN.md §7ad.
 *
 * keydesign-addon loads the API with `&loading=async`, which makes
 * `google.maps` a bootstrap stub: only `importLibrary` is real until some
 * library has been awaited. Its own init then does
 *
 *     var loc = new google.maps.LatLng(lat, lng);            // synchronous
 *     const { AdvancedMarkerElement } =
 *         await google.maps.importLibrary('marker');         // the await
 *
 * — constructing a `LatLng` on the line *before* the await that would have
 * populated it. Whether the constructor exists by `jQuery(window).on('load')`
 * is pure timing: measured across four cold loads on this machine it threw
 * `TypeError: google.maps.LatLng is not a constructor` twice, leaving an
 * empty grey band, and rendered twice.
 *
 * This cannot be fixed by loading earlier — the Maps script is enqueued
 * *after* this file — so it is a recovery path instead: once the addon has had
 * its turn, if the container is still empty, await the core library and call
 * its init again. Zero cost when the addon wins the race.
 *
 * The init is a global named `initKdMap_<uid>`, one per element on the page.
 */
function initMapRepair() {
	const containers = document.querySelectorAll('.contact-map-container');

	if (!containers.length) {
		return;
	}

	window.addEventListener('load', () => {
		// After every other `load` handler, the addon's included.
		window.setTimeout(() => {
			// `.gm-style` is what the Maps API puts on its own root, so its
			// absence means the init threw rather than merely rendered slowly.
			const rendered = [...containers].every((el) => el.querySelector('.gm-style'));

			if (rendered) {
				return;
			}

			const maps = window.google && window.google.maps;

			if (!maps || typeof maps.importLibrary !== 'function') {
				return;
			}

			maps.importLibrary('core').then(() => {
				Object.keys(window)
					.filter((key) => key.indexOf('initKdMap_') === 0 && typeof window[key] === 'function')
					.forEach((key) => {
						try {
							window[key]();
						} catch (error) {
							// One dead map is not worth breaking the page for.
						}
					});
			});
		}, 0);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMapRepair);
} else {
	initMapRepair();
}
